import { useState } from "react"
import { MoreHorizontal, Trash2, ShieldAlert, ChevronDown, Loader2 } from "lucide-react"
import { GradientSparkleIcon } from "@/components/extraction/GradientSparkleIcon"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Badge } from "@/components/ui/badge"
import { ScoreBar } from "@/components/strategy/ScoreBar"
import { AiRationaleBlock } from "@/components/strategy/AiRationaleBlock"
import { cn } from "@/lib/utils"
import type { GoNoGoCriterion, AiScoringSource } from "@/stores/strategy-store"

interface GoNoGoCriterionCardProps {
  criterion: GoNoGoCriterion
  onScoreChange: (score: number | null) => void
  onEvidenceChange: (evidence: string) => void
  onWeightChange?: ((weight: number) => void) | undefined
  onRemove?: (() => void) | undefined
  onAiScore?: (() => void) | undefined
  readOnly?: boolean
  canEditStructure?: boolean
  scoringSources?: AiScoringSource[] | null | undefined
}

const WEIGHT_PRESETS: { label: string; value: number }[] = [
  { label: "Low", value: 2 },
  { label: "Medium", value: 5 },
  { label: "High", value: 8 },
]

function weightLabel(weight: number): string {
  const preset = WEIGHT_PRESETS.find(p => p.value === weight)
  return preset ? preset.label : `Custom (${weight})`
}

export function GoNoGoCriterionCard({
  criterion,
  onScoreChange,
  onEvidenceChange,
  onWeightChange,
  onRemove,
  onAiScore,
  readOnly = false,
  canEditStructure = false,
  scoringSources,
}: GoNoGoCriterionCardProps) {
  const [weightOpen, setWeightOpen] = useState(false)
  const [showCustomWeight, setShowCustomWeight] = useState(false)
  const hasAiRationale = criterion.aiRationale != null
  const isScoring = criterion.aiScoringStatus === 'scoring'
  const aiScoreLabel = hasAiRationale ? 'Re-score with AI' : 'Score with AI'
  // The AI assessed this one and declined to score it for lack of evidence, and
  // the user hasn't scored it either — this is unambiguously their work.
  const needsManualScore =
    hasAiRationale && criterion.aiSuggestedScore == null && criterion.score == null

  const handleWeightSelect = (value: number) => {
    onWeightChange?.(value)
    setWeightOpen(false)
    setShowCustomWeight(false)
  }

  return (
    <div className="p-4 space-y-3 group">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{criterion.title}</span>
          {criterion.isMandatory && (
            <Badge variant="outline" className="gap-1 font-normal text-[11px]">
              <ShieldAlert className="h-3 w-3" />
              Mandatory
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          {canEditStructure ? (
            <Popover
              open={weightOpen}
              onOpenChange={(open) => {
                setWeightOpen(open)
                if (!open) setShowCustomWeight(false)
              }}
            >
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1 text-meta px-1.5 py-0.5 rounded hover:bg-muted hover:text-foreground cursor-pointer border border-transparent hover:border-border transition-colors"
                >
                  Weight: {weightLabel(criterion.weight)}
                  <ChevronDown className="h-3 w-3" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-44 p-1.5">
                {!showCustomWeight ? (
                  <div className="flex flex-col gap-0.5">
                    {WEIGHT_PRESETS.map(preset => (
                      <button
                        key={preset.value}
                        type="button"
                        onClick={() => handleWeightSelect(preset.value)}
                        className={cn(
                          "flex items-center justify-between rounded-md px-2 py-1.5 text-sm hover:bg-muted",
                          criterion.weight === preset.value && "bg-muted font-medium",
                        )}
                      >
                        <span>{preset.label}</span>
                        <span className="text-meta">{preset.value}</span>
                      </button>
                    ))}
                    <div className="my-1 h-px bg-border" />
                    <button
                      type="button"
                      onClick={() => setShowCustomWeight(true)}
                      className="rounded-md px-2 py-1.5 text-sm text-left text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      Custom…
                    </button>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <div className="text-meta px-1">Weight (1–10)</div>
                    <div className="grid grid-cols-5 gap-1">
                      {Array.from({ length: 10 }, (_, i) => i + 1).map(w => (
                        <button
                          key={w}
                          type="button"
                          onClick={() => handleWeightSelect(w)}
                          className={cn(
                            "h-7 rounded text-xs border transition-colors",
                            w === criterion.weight
                              ? "bg-foreground text-background border-transparent font-semibold"
                              : "border-border text-muted-foreground hover:bg-muted",
                          )}
                        >
                          {w}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          ) : (
            <span className="text-meta px-1.5 py-0.5">
              Weight: {weightLabel(criterion.weight)}
            </span>
          )}

          {onAiScore && !readOnly && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={onAiScore}
                    disabled={isScoring}
                    aria-label={aiScoreLabel}
                  >
                    {isScoring
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <GradientSparkleIcon className="h-3.5 w-3.5" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{isScoring ? 'Scoring with AI…' : aiScoreLabel}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {canEditStructure && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem>
                  <ShieldAlert className="h-3.5 w-3.5 mr-2" />
                  {criterion.isMandatory ? 'Remove mandatory flag' : 'Mark as mandatory'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onRemove} className="text-destructive focus:text-destructive">
                  <Trash2 className="h-3.5 w-3.5 mr-2" />
                  Remove criterion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Score row */}
      <div className="flex items-center gap-2">
        <ScoreBar
          value={criterion.score}
          {...(!readOnly && onScoreChange ? { onChange: onScoreChange } : {})}
          readOnly={readOnly}
          semantic
          aiSuggestedScore={criterion.aiSuggestedScore}
        />
        {needsManualScore && (
          <Badge variant="warning" className="font-normal text-[11px]" dot>
            AI: insufficient evidence
          </Badge>
        )}
      </div>

      {/* AI Rationale (or scoring placeholder) */}
      {hasAiRationale ? (
        <AiRationaleBlock
          rationale={criterion.aiRationale!}
          suggestedScore={criterion.aiSuggestedScore}
          confidence={criterion.aiConfidence}
          currentScore={criterion.score}
          scoringSources={scoringSources}
          isLoading={isScoring}
          {...(!readOnly ? { onApplyScore: onScoreChange } : {})}
        />
      ) : isScoring ? (
        <AiRationaleBlock
          rationale=""
          suggestedScore={null}
          confidence={null}
          isLoading
        />
      ) : null}

      {/* Evidence / Notes */}
      {!readOnly && (
        <textarea
          className="w-full border border-border rounded-lg p-2.5 text-sm bg-transparent resize-none placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          rows={criterion.evidence ? 2 : 1}
          placeholder="Notes (optional)"
          value={criterion.evidence ?? ''}
          onChange={(e) => onEvidenceChange(e.target.value)}
        />
      )}
      {readOnly && criterion.evidence && (
        <p className="text-sm text-muted-foreground">{criterion.evidence}</p>
      )}
    </div>
  )
}
